import { RootState } from 'Data/Redux';
import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { CalculateFileSizeFromBase64, ExtractBase64FromDataUrl, fromFileAsDataUrl } from 'Lib/File';
import { ImageResize, TiffToPngDataUrl } from 'Lib/Image';
import { switchMapByKey } from 'Lib/Rxjs/operators';
import { Epic, StateObservable, ofType } from 'redux-observable';
import { Observable, from, iif, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { actions } from './slice';
import { ProcessImageAction, ProcessImageRejectAction, ProcessImageResolveAction } from './types';

export const raiseNoticeOnProcessImageReject: Epic = (action$) =>
  action$.pipe(
    ofType(actions.processImageReject.toString()),
    mergeMap((action: ProcessImageRejectAction) =>
      of(noticeActions.addNotice({ severity: 'error', message: action.payload.error }))
    )
  );

export const processImage: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.processImage.toString()),
    switchMapByKey(
      (action: ProcessImageAction) => action.payload.id,
      (action: ProcessImageAction): Observable<ProcessImageRejectAction | ProcessImageResolveAction> => {
        const { fileUrl, filetype, fileSizeInBytes, resizeSettings } = action.payload;

        if (!hasValue(filetype) || !filetype!.match(/image.*/)) {
          return of(
            actions.processImageReject({
              id: action.payload.id,
              error: 'Failed to process image: Unsupported file type.',
            })
          );
        }

        // Get the image as a usable data uri (converting if required)
        return iif(
          () => filetype === 'image/tiff',
          from(fetch(fileUrl!).then((r) => r.arrayBuffer())).pipe(
            map((a) => ({ imageData: TiffToPngDataUrl(a), fileType: 'image/png' })),
            catchError((_) => {
              throw new Error(`Failed to process image: TIFF file could not be converted to a supported format.`);
            })
          ),
          from(fetch(fileUrl!).then((r) => r.blob())).pipe(
            switchMap((b) =>
              fromFileAsDataUrl(b).pipe(
                map((a) => ({ imageData: a, fileType: filetype! })),
                catchError((_) => {
                  throw new Error(`Failed to process image: Could not obtain file.`);
                })
              )
            ),
            catchError((_) => {
              throw new Error(`Failed to process image: Could not obtain file.`);
            })
          )
        ).pipe(
          switchMap((d) => {
            if (hasNonEmptyValue(resizeSettings)) {
              return from(
                ImageResize({
                  imageDataUrl: d.imageData,
                  imageType: d.fileType,
                  resizeSettings: resizeSettings!,
                })
              ).pipe(
                switchMap((summary) => {
                  const resizedFileSizeInBytes = CalculateFileSizeFromBase64(
                    ExtractBase64FromDataUrl(summary.ResizedData)
                  );

                  // If resizing produces a larger image we take the original image as the resized image.
                  if (resizedFileSizeInBytes > fileSizeInBytes!)
                    return of(
                      actions.processImageResolve({
                        id: action.payload.id,
                        processedDataUrl: d.imageData,
                        resizing: {
                          originalSize: {
                            width: summary.OriginalWidth,
                            height: summary.OriginalHeight,
                            fileSizeInBytes: fileSizeInBytes!,
                          },
                          resizedSize: {
                            width: summary.OriginalWidth,
                            height: summary.OriginalHeight,
                            fileSizeInBytes: fileSizeInBytes!,
                          },
                        },
                      })
                    );

                  return of(
                    actions.processImageResolve({
                      id: action.payload.id,
                      processedDataUrl: summary.ResizedData,
                      resizing: {
                        originalSize: {
                          width: summary.OriginalWidth,
                          height: summary.OriginalHeight,
                          fileSizeInBytes: fileSizeInBytes!,
                        },
                        resizedSize: {
                          width: summary.ResizedWidth,
                          height: summary.ResizedHeight,
                          fileSizeInBytes: resizedFileSizeInBytes,
                        },
                      },
                    })
                  );
                }),
                catchError((_) => {
                  throw new Error(`Failed to process image: Image could not be resized.`);
                })
              );
            }

            return of(
              actions.processImageResolve({
                id: action.payload.id,
                processedDataUrl: d.imageData,
              })
            );
          }),
          catchError((e) =>
            of(
              actions.processImageReject({
                id: action.payload.id,
                error: e.message,
              })
            )
          )
        );
      }
    )
  );
